/**
 * Hook para cargar y cachear entitlements de roles.
 *
 * El caché vive mientras el componente esté montado.
 * Usar una instancia por modal/contexto para que el caché
 * se limpie automáticamente al desmontar.
 */
import { useState, useCallback } from 'react'
import { listRoleEntitlements } from '@/features/ksec/api'
import type { KsecEntitlement } from '@/features/ksec/types'

export interface RoleEntitlementsState {
  cache: Record<string, KsecEntitlement[]>
  loadingIds: Set<string>
  loadFor: (roleId: string) => Promise<KsecEntitlement[]>
  isLoading: (roleId: string) => boolean
  get: (roleId: string) => KsecEntitlement[] | undefined
}

export function useRoleEntitlements(): RoleEntitlementsState {
  const [cache, setCache] = useState<Record<string, KsecEntitlement[]>>({})
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())

  const loadFor = useCallback(
    async (roleId: string): Promise<KsecEntitlement[]> => {
      if (cache[roleId] !== undefined) return cache[roleId]

      setLoadingIds((prev) => new Set([...prev, roleId]))
      try {
        const res = await listRoleEntitlements(roleId)
        const items: KsecEntitlement[] = Array.isArray(res.data) ? res.data : []
        setCache((prev) => ({ ...prev, [roleId]: items }))
        return items
      } catch {
        setCache((prev) => ({ ...prev, [roleId]: [] }))
        return []
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev)
          next.delete(roleId)
          return next
        })
      }
    },
    [cache],
  )

  const isLoading = useCallback((roleId: string) => loadingIds.has(roleId), [loadingIds])
  const get = useCallback((roleId: string) => cache[roleId], [cache])

  return { cache, loadingIds, loadFor, isLoading, get }
}
